import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import SimplifiedTenseAndPOV from "src/components/StoryBible/TenseAndPOV/Simplified";
import { useProjectStore } from "src/stores/projectStore";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { POVS, isAutoPOV } from "../../../../../../src/shared/constants";
import { sendAppEvent } from "../../../../Utils";

export const POVTenseButtons = ({ draftStateMachine, povTense, setPOVTense, className, projectId, disableContainerQuery = false, allowWrap = false, disabled = false }) => {
  const isMultiplePOVState = useProjectStore((state) => state.isMultiplePOV);
  const isMultiplePOV = isMultiplePOVState?.value || false;
  const currentDocumentPOVTense = useProjectStore((state) => state.currentDocumentPOVTense);
  const setPreflightResponse = useStoryBibleStore((state) => state.setPreflightResponse);
  const projectPOV = useProjectStore((state) => state.povTense?.pov?.value);
  const projectPOVCharacters = useProjectStore((state) => state.povTense?.pov?.characters);
  const projectTense = useProjectStore((state) => state.povTense?.tense);
  const setPOVTenseForProject = useProjectStore((state) => state.setPOVTense);
  const setPOVTenseForDocument = useProjectStore((state) => state.setPOVTenseForDocument);

  const handleUpdateProjectPOV = async (selectedOption) => {
    sendAppEvent("changing_project_pov_from_generate_panel", {
      isMultiplePOV,
      pov: selectedOption,
      projectId,
    });

    const isAutoPOVSelected = isAutoPOV(selectedOption);
    const hasCharacterSelection = povTense?.pov?.characters?.length > 0;

    try {
      if (!isMultiplePOV) {
        await setPOVTenseForProject({
          ...povTense,
          pov: { ...povTense.pov, value: selectedOption, characters: isAutoPOVSelected && hasCharacterSelection ? [] : povTense.pov.characters, userChanged: true },
        });
      } else {
        await setPOVTenseForDocument({
          pov: { ...povTense.pov, value: selectedOption, characters: isAutoPOVSelected && hasCharacterSelection ? [] : povTense.pov.characters, userChanged: true },
          tense: povTense.tense,
        });
      }
      setPreflightResponse(null);
      draftStateMachine.events.reset();
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: "structured_outline_pov_update" } });
    }
  };

  const handleUpdateProjectTense = async (selectedOption) => {
    sendAppEvent("changing_project_tense_from_generate_panel", {
      isMultiplePOV,
      tense: selectedOption,
      projectId,
    });

    try {
      if (!isMultiplePOV) {
        await setPOVTenseForProject({ ...povTense, tense: selectedOption });
      } else {
        await setPOVTenseForDocument({
          ...povTense,
          tense: selectedOption,
        });
      }
      setPreflightResponse(null);
      draftStateMachine.events.reset();
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: "structured_outline_tense_update" } });
    }
  };

  const handleCharacterSelectChange = async (selectedOptions) => {
    sendAppEvent("changing_project_characters_from_generate_panel", {
      isMultiplePOV,
      characters: selectedOptions,
      projectId,
    });

    const isAutoPOVSelected = isAutoPOV(povTense.pov.value);
    const hasCharacterSelection = selectedOptions.length > 0;

    try {
      if (!isMultiplePOV) {
        await setPOVTenseForProject({
          ...povTense,
          pov: {
            ...povTense.pov,
            value: isAutoPOVSelected && hasCharacterSelection ? POVS.THIRD_PERSON_LIMITED : povTense.pov.value,
            characters: selectedOptions,
            userChanged: true,
          },
        });
      } else {
        await setPOVTenseForDocument({
          ...povTense,
          pov: {
            ...povTense.pov,
            value: isAutoPOVSelected && hasCharacterSelection ? POVS.THIRD_PERSON_LIMITED : povTense.pov.value,
            characters: selectedOptions,
            userChanged: true,
          },
        });
      }
      setPreflightResponse(null);
      draftStateMachine.events.reset();
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: "structured_outline_character_update" } });
    }
  };

  useEffect(() => {
    const getPOVTenseForDocument = async () => {
      if (isMultiplePOV) {
        setPOVTense(currentDocumentPOVTense);
      } else {
        if (!projectPOV || !projectTense) {
          return;
        }

        setPOVTense({
          pov: { value: projectPOV, characters: projectPOVCharacters },
          tense: { value: projectTense.value },
        });
      }
    };

    getPOVTenseForDocument();
  }, [isMultiplePOV, projectPOV, projectPOVCharacters, projectTense, setPOVTense, currentDocumentPOVTense]);

  return (
    <div
      className={`w-full flex self-start flex-row gap-3 items-center flex-wrap @3xl:flex-nowrap ${className}`}
      style={{
        ...(disableContainerQuery ? {} : { containerType: "inline-size", contain: "layout" }),
        ...(disabled ? { pointerEvents: "none", opacity: 0.5 } : {}),
      }}
    >
      <SimplifiedTenseAndPOV
        pov={povTense.pov}
        tense={povTense.tense}
        handleManualPOVChange={handleUpdateProjectPOV}
        handleManualTenseChange={handleUpdateProjectTense}
        handleCharacterSelectChange={handleCharacterSelectChange}
        oneOrManyCharacters="many"
        showLabel={false}
        collisionBoundarySelector="#editor-section"
      />
    </div>
  );
};
